import { Accordion, AccordionDetails, AccordionSummary, Button } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React, { useState } from 'react';
import ExperienceTable from '../inputs/ExperienceTable';
import TextInputField from '../inputs/TextInputField';
import WillingToTravelFields from '../inputs/WillingToTravelFields';
import { MAX_CHARS } from '../constants/AppraiserInfoConstants';
import { useSelectionListOptions } from '../hooks/useSelectionListOptions';
import ApolloError from '@shared/components/ApolloError';
import CustomDatePicker from '@shared/components/CustomDatePicker';
import CustomSelector from '@shared/components/CustomSelector';
import MuiInputTextWithRemainingChars from '@shared/components/MuiInputTextWithRemainingChars';
import ErrorMessage from '@shared/components/ErrorMessage';
import LinkButton from '@shared/components/LinkButton';
import SuccessMessage from '@shared/components/SuccessMessage';
import SkeletonWrapper from '@shared/components/skeleton/SkeletonWrapper';
import AppraiserInputValidator from '../validators/AppraiserInputValidator';
import { isAtlAffiliationEnabled, isAtmAffiliationEnabled } from '../services/AffiliationService';
import * as systemRoles from '../../../constants/system-roles';
import YtdAppraisalStatistics from './shared/YtdAppraisalStatistics';
import { showToast } from '@shared/services/toastService';
import { template } from 'lodash';
import { TOAST_MESSAGE_TYPES } from '@shared/constants/toastMessageTypes';
import { VALIDATION_ERROR_CODES } from '../constants/validationErrorCodes';
import AnimatedWrapper from '@shared/components/animations/AnimatedWrapper';

const OBSERVER_ENABLED_ROLES = [
  systemRoles.ORG_SPONSOR,
  systemRoles.ORG_COORDINATOR,
  systemRoles.ORG_UNIT_SPONSOR,
  systemRoles.ORG_UNIT_COORDINATOR,
  systemRoles.ISACA_MDDAP_APPRAISER
];

const ADDITIONAL_INFORMATION_MAX_CHARS = 150;

/**
 * @typedef {object} AppraiserFormProps
 * @property {boolean} [isAppraiser]
 * @property {Object} [appraiserFormData]
 * @property {Object} [appraiserNonEditableData]
 * @property {boolean} [appraiserInfoLoading]
 * @property {boolean} [canEditDirectChannel]
 * @property {(appraiserFormData: Object | ((prev: Object) => Object)) => void} [setAppraiserFormData]
 * @property {(editedDomainExperiences: Array<{ domain: number, role: number, yearsOfExperience: number | string }>) => void | Promise<void>} [saveAppraiser]
 * @property {boolean} [isFormEdited]
 * @property {string} [editSuccessMessage]
 * @property {string} [previousLink]
 * @property {boolean} [canEditObservation]
 * @property {boolean} [canEditAdditionalInformation]
 */

/**
 * @param {AppraiserFormProps} props
 */
const AppraiserForm = props => {
  const {
    isAppraiser,
    appraiserFormData,
    appraiserNonEditableData,
    setAppraiserFormData,
    appraiserInfoLoading,
    saveAppraiser,
    isFormEdited,
    editSuccessMessage = '',
    previousLink,
    canEditDirectChannel,
    canEditObservation,
    canEditAdditionalInformation
  } = props;

  const [editedDomainExperiences, setEditedDomainExperiences] = useState([]);
  const [sectionVisibility, setSectionVisibility] = useState({
    showPersonalSection: true,
    showExperienceSection: true
  });
  const emptyDateText = 'YYYY/MM/DD';

  const {
    desiredRoleOptions,
    currentRoleOptions,
    timeZoneOptions,
    directChannelOptions,
    observationOptions,
    selectionListError,
    selectionLisLoading
  } = useSelectionListOptions(appraiserNonEditableData.systemRole?.id);

  const toggleSectionVisibility = section => {
    setSectionVisibility(prevState => ({
      ...prevState,
      [section]: !prevState[section]
    }));
  };

  const handleFieldChange = event => {
    const { name, value } = event.target;
    setValue(name, value);
  };

  const getCurrentRoleOption = value => {
    return currentRoleOptions.find(option => option.value === value);
  };

  const setCurrentRole = value => {
    const affiliationType = getCurrentRoleOption(value).affiliationType;

    setAppraiserFormData({
      ...appraiserFormData,
      currentRole: value,
      affiliationType: affiliationType,
      atlAffiliation: isAtlAffiliationEnabled(affiliationType)
        ? appraiserFormData.atlAffiliation
        : null,
      atmAffiliation: isAtmAffiliationEnabled(affiliationType)
        ? appraiserFormData.atmAffiliation
        : null
    });
  };

  const setValue = (name, value) => {
    setAppraiserFormData({ ...appraiserFormData, [name]: value });
  };

  const setExperienceValue = (domainId, role, value) => {
    const updatedExperiences = appraiserFormData.domainExperiences;
    updatedExperiences.find(
      item => item.domain === domainId && item.role === role
    ).yearsOfExperience = value;

    setAppraiserFormData({ ...appraiserFormData, domainExperiences: updatedExperiences });

    const editedExperiences = [...editedDomainExperiences];
    const existingExperience = editedExperiences.find(
      experience => experience.domain === domainId && experience.role === role
    );

    if (existingExperience) {
      existingExperience.yearsOfExperience = value;
      setEditedDomainExperiences(editedExperiences);
      return;
    }

    editedExperiences.push({ domain: domainId, role: role, yearsOfExperience: value });
    setEditedDomainExperiences(editedExperiences);
  };

  const handleSaveBtnClick = async () => {
    const validationResult =
      AppraiserInputValidator.validateEditAppraiserLicenseData(appraiserFormData);

    if (validationResult.error) {
      const errorMessage = template(validationResult.error.message)({
        currentRoleName: currentRoleOption?.title
      });

      const isObserverRoleWarning = validationResult.error.details.some(
        detail => detail.type === VALIDATION_ERROR_CODES.OBSERVER_ROLE_NOT_ELIGIBLE
      );

      showToast(
        isObserverRoleWarning ? TOAST_MESSAGE_TYPES.WARNING : TOAST_MESSAGE_TYPES.ERROR,
        errorMessage
      );
      return false;
    }

    await saveAppraiser(editedDomainExperiences);
  };

  if (selectionListError) {
    return <ApolloError />;
  }

  const currentRoleOption = getCurrentRoleOption(appraiserFormData.currentRole);
  const isObservationRoleEnabled = OBSERVER_ENABLED_ROLES.includes(
    appraiserNonEditableData.systemRole?.id
  );

  return (
    <>
      <div className='page-header-section'>
        <div className='appraiser-info'>
          <div>
            <div className='affiliated-date' data-testid='atm-affiliation-date'>
              <p className='bold'>ATM Affiliation:</p>
              <SkeletonWrapper show={appraiserInfoLoading} inheritParentSize={true}>
                {currentRoleOption && isAtmAffiliationEnabled(currentRoleOption.affiliationType) ? (
                  <CustomDatePicker
                    id='atm-affiliation-date'
                    selectedDate={appraiserFormData.atmAffiliation}
                    setSelectedDate={value => setValue('atmAffiliation', value)}
                    disabled={isAppraiser}
                    dateFormat='yyyy-MM-dd'
                  />
                ) : (
                  <span>{emptyDateText}</span>
                )}
              </SkeletonWrapper>
            </div>
            <AnimatedWrapper
              show={
                !!currentRoleOption && isAtlAffiliationEnabled(currentRoleOption.affiliationType)
              }
              className='affiliated-date'
              data-testid='atl-affiliation-date'>
              <p className='bold'>ATL Affiliation:</p>
              <CustomDatePicker
                id='atl-affiliation-date'
                selectedDate={appraiserFormData.atlAffiliation}
                setSelectedDate={value => setValue('atlAffiliation', value)}
                disabled={isAppraiser}
                dateFormat='yyyy-MM-dd'
              />
            </AnimatedWrapper>
          </div>
          <YtdAppraisalStatistics
            appraiserNonEditableData={appraiserNonEditableData}
            appraiserInfoLoading={appraiserInfoLoading}
          />
        </div>
        <div className='action-group'>
          <div className='action-button-group'>
            <LinkButton className='secondary-button sm' to={previousLink || -1}>
              Back
            </LinkButton>
            <Button
              type='submit'
              disabled={!isFormEdited}
              onClick={handleSaveBtnClick}
              className='primary-button sm'>
              Save
            </Button>
          </div>

          {isFormEdited && (
            <ErrorMessage
              className='error-message'
              message='There are unsaved changes on this page.'
            />
          )}
          {editSuccessMessage && <SuccessMessage message={editSuccessMessage} />}
        </div>
      </div>
      <Accordion
        expanded={sectionVisibility.showPersonalSection}
        className='personal-section'
        slotProps={{
          transition: { unmountOnExit: true }
        }}>
        <AccordionSummary
          onClick={() => toggleSectionVisibility('showPersonalSection')}
          expandIcon={<ExpandMoreIcon data-testid='personal-expand-icon' />}>
          <h3>Personal Information</h3>
        </AccordionSummary>
        <AccordionDetails data-testid='personal-section-details'>
          <SkeletonWrapper show={appraiserInfoLoading} in>
            <div className='input-group-container'>
              <div className='input-group'>
                <div>
                  <TextInputField
                    id='company'
                    name='company'
                    title='Company'
                    value={appraiserNonEditableData.company}
                    disabled={true}
                    placeholder='Enter Company'
                  />
                </div>
                <div>
                  <TextInputField
                    id='email'
                    name='email'
                    title='Email'
                    value={appraiserNonEditableData.email}
                    disabled={true}
                    placeholder='Enter Email'
                  />
                </div>
              </div>
              <div className='input-group'>
                <div>
                  <TextInputField
                    id='city'
                    name='city'
                    title='City'
                    placeholder='Enter City'
                    value={appraiserFormData.city}
                    handleChange={handleFieldChange}
                    maxLength={MAX_CHARS}
                  />
                </div>
                <div>
                  <TextInputField
                    id='state'
                    name='state'
                    title='State'
                    placeholder='Enter State/Province'
                    value={appraiserFormData.state}
                    handleChange={handleFieldChange}
                    maxLength={MAX_CHARS}
                  />
                </div>
              </div>
              <div className='input-group'>
                <div>
                  <TextInputField
                    id='country'
                    name='country'
                    title='Country'
                    placeholder='Enter Country'
                    value={appraiserFormData.country}
                    handleChange={handleFieldChange}
                    maxLength={MAX_CHARS}
                  />
                </div>
                <div>
                  <CustomSelector
                    id='time-zones-selector'
                    title='Time Zone'
                    placeholder='Select a Time Zone'
                    options={timeZoneOptions}
                    value={appraiserFormData.timeZoneId}
                    setValue={value => setValue('timeZoneId', value)}
                    testId='time-zones-selector'
                    loading={selectionLisLoading}
                  />
                </div>
              </div>
              <div className='input-group'>
                <div>
                  <CustomSelector
                    id='desired-roles-selector'
                    title='Desired MDDAP Role'
                    placeholder='Select a Role'
                    options={desiredRoleOptions}
                    value={appraiserFormData.desiredRole}
                    setValue={value => setValue('desiredRole', value)}
                    testId='desired-roles-selector'
                    loading={selectionLisLoading}
                  />
                </div>
                <div>
                  <CustomSelector
                    id='current-roles-selector'
                    title='Current MDDAP Role'
                    placeholder='Select a Role'
                    options={currentRoleOptions}
                    value={appraiserFormData.currentRole}
                    setValue={setCurrentRole}
                    testId='current-roles-selector'
                    loading={selectionLisLoading}
                    disabled={isAppraiser}
                  />
                </div>
              </div>
              <div className='input-group'>
                <div>
                  <CustomSelector
                    id='direct-channel-selector'
                    title='Direct Channel'
                    options={directChannelOptions}
                    value={appraiserFormData.directChannel}
                    setValue={value => setValue('directChannel', value)}
                    testId='direct-channel-selector'
                    loading={selectionLisLoading}
                    disabled={!canEditDirectChannel}
                  />
                </div>
                <div>
                  <CustomSelector
                    id='observation-selector'
                    title='Observation'
                    options={observationOptions}
                    value={appraiserFormData.observation}
                    setValue={value => setValue('observation', value)}
                    testId='observation-selector'
                    loading={selectionLisLoading}
                    disabled={!canEditObservation || !isObservationRoleEnabled}
                  />
                </div>
              </div>
              <div>
                <h4 className='field-title'>Additional Information</h4>
                <MuiInputTextWithRemainingChars
                  id='additional-information'
                  className='additional-information'
                  name='additionalInformation'
                  fullWidth
                  disableUnderline
                  multiline={true}
                  rows={3}
                  placeholder='To be shared with ATLs when building a team'
                  value={appraiserFormData.additionalInformation}
                  onChange={handleFieldChange}
                  maxLength={ADDITIONAL_INFORMATION_MAX_CHARS}
                  disabled={!canEditAdditionalInformation}
                />
              </div>
            </div>
            <div>
              <WillingToTravelFields appraiserData={appraiserFormData} setValue={setValue} />
            </div>
          </SkeletonWrapper>
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={sectionVisibility.showExperienceSection}
        className='experience-section'
        slotProps={{
          transition: { unmountOnExit: true }
        }}>
        <AccordionSummary
          onClick={() => toggleSectionVisibility('showExperienceSection')}
          expandIcon={<ExpandMoreIcon data-testid='experience-expand-icon' />}>
          <h3>Experience</h3>
        </AccordionSummary>
        <AccordionDetails data-testid='experience-section-details'>
          <SkeletonWrapper show={appraiserInfoLoading}>
            <ExperienceTable
              appraiserData={appraiserFormData}
              setValue={setExperienceValue}
              showAtmAtlRows={true}
              disableEditAscDomain={isAppraiser}
              hideAscDomainColumn={
                appraiserNonEditableData.systemRole?.id !== systemRoles.ISACA_MDDAP_APPRAISER
              }
            />
          </SkeletonWrapper>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default AppraiserForm;
